import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { Avatar, PriorityFlag, StatusPill } from '@/components/Chips'
import { Icon } from '@/components/Icon'
import { fetchTicket, promoteTicket, recomputeTicketClock } from '@/features/support/api'
import type { ApiError } from '@/lib/api/client'
import type { Priority, StateCategory } from '@/types/domain'
import type { Ticket } from '@/types/support'

/**
 * One ticket, opened over the desk.
 *
 * The desk shows a clock and, until now, no way to look behind it — an overdue
 * first response could be seen and not investigated, which on a support screen
 * means a client is waiting and nobody can find out why.
 *
 * Both threads are shown separately and labelled. The server builds each
 * audience's payload from its own allow-list rather than by removing fields, so
 * the internal notes are simply absent from a guest's response; showing them
 * under a heading that says who can read them is the honest rendering of that.
 */
export function TicketPanel({
  ticketKey,
  asOf,
  onClose,
}: {
  ticketKey: string
  asOf: number
  onClose: () => void
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const ticket = useQuery<Ticket, ApiError>({
    queryKey: ['ticket', ticketKey],
    queryFn: () => fetchTicket(ticketKey),
    retry: false,
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['ticket', ticketKey] })
    void queryClient.invalidateQueries({ queryKey: ['tickets'] })
  }

  const promote = useMutation<Ticket, ApiError, void>({
    mutationFn: () => promoteTicket(ticketKey),
    onSuccess: refresh,
  })

  const recompute = useMutation<Ticket, ApiError, void>({
    mutationFn: () => recomputeTicketClock(ticketKey),
    onSuccess: refresh,
  })

  const data = ticket.data

  return (
    <>
      <button
        type="button"
        aria-label={t('view.close')}
        onClick={onClose}
        className="fixed inset-0 z-40 bg-black/20"
      />

      <aside className="border-line bg-surface fixed inset-y-0 end-0 z-50 flex w-full max-w-xl flex-col border-s shadow-2xl">
        <header className="border-line flex items-start gap-3 border-b px-5 py-4">
          <div className="min-w-0 flex-1">
            <span className="text-brand-600 font-mono text-[11px]">{ticketKey}</span>
            <h2 className="text-ink-900 mt-0.5 text-lg leading-snug font-semibold">
              {data?.title ?? t('common.loading')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            title={t('view.close')}
            className="text-ink-500 hover:bg-surface-2 hover:text-ink-900 rounded p-1.5"
          >
            <Icon name="close" className="size-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {ticket.isError ? (
            <p className="text-[13px] text-[color:var(--color-state-blocked)]">
              {ticket.error.message}
            </p>
          ) : data === undefined ? (
            <p className="text-ink-500 font-mono text-[11px]">{t('common.loading')}</p>
          ) : (
            <>
              <dl className="grid grid-cols-[110px_minmax(0,1fr)] items-center gap-y-2.5 text-[13px]">
                <dt className="text-ink-500">{t('support.status')}</dt>
                <dd>
                  <StatusPill
                    name={data.status?.name ?? null}
                    category={(data.status?.category ?? null) as StateCategory | null}
                    compact
                  />
                </dd>

                <dt className="text-ink-500">{t('support.priority')}</dt>
                <dd className="flex items-center gap-1.5">
                  <PriorityFlag priority={data.priority as Priority} />
                  <span className="text-ink-700">{t(`priority.${data.priority}`)}</span>
                </dd>

                <dt className="text-ink-500">{t('support.project')}</dt>
                <dd>
                  {data.project == null ? (
                    <span className="text-ink-500">—</span>
                  ) : (
                    <Link to={`/projects/${data.project.key}`} className="text-brand-600 text-[13px]">
                      {data.project.key} — {data.project.name}
                    </Link>
                  )}
                </dd>

                <dt className="text-ink-500">{t('support.contract')}</dt>
                <dd className="text-ink-700">{data.contract?.name ?? '—'}</dd>

                <dt className="text-ink-500">{t('support.reporter')}</dt>
                <dd className="text-ink-700 break-all">{data.reporterEmail ?? '—'}</dd>

                <dt className="text-ink-500">{t('support.assignee')}</dt>
                <dd className="flex items-center gap-2">
                  <Avatar name={data.assignee?.name ?? null} color={data.assignee?.color} size="sm" />
                  <span className="text-ink-700">{data.assignee?.name ?? t('work.unassigned')}</span>
                </dd>

                <dt className="text-ink-500">{t('support.channel')}</dt>
                <dd className="text-ink-700 font-mono text-[12px]">{data.channel ?? '—'}</dd>
              </dl>

              <Clocks ticket={data} asOf={asOf} />

              {data.description === null || data.description === '' ? null : (
                <p className="text-ink-700 border-line mt-4 border-t pt-4 text-sm whitespace-pre-line">
                  {data.description}
                </p>
              )}

              <Thread
                title={t('support.clientThread')}
                tone="client"
                rows={data.comments}
              />
              <Thread
                title={t('support.internalNotes')}
                tone="internal"
                rows={data.internalComments ?? []}
              />

              {data.bug == null ? null : (
                <p className="border-line mt-5 flex items-center gap-2 border-t pt-4 text-[13px]">
                  <Icon name="link" className="text-ink-500 size-3.5" />
                  <span className="text-ink-500">{t('support.promoted')}</span>
                  <span className="text-brand-600 font-mono text-[11px]">{data.bug.key}</span>
                </p>
              )}
            </>
          )}
        </div>

        {data === undefined ? null : (
          <footer className="border-line bg-surface-2 flex flex-wrap items-center gap-2 border-t px-5 py-3">
            <button
              type="button"
              onClick={() => promote.mutate()}
              disabled={promote.isPending || data.bug != null}
              className="border-line text-ink-700 hover:bg-surface flex items-center gap-1.5 rounded border px-3 py-1.5 text-[12px] disabled:opacity-40"
            >
              <Icon name="ticket" className="size-3.5" />
              {t('support.promote')}
            </button>

            <button
              type="button"
              onClick={() => recompute.mutate()}
              disabled={recompute.isPending}
              className="border-line text-ink-700 hover:bg-surface flex items-center gap-1.5 rounded border px-3 py-1.5 text-[12px] disabled:opacity-40"
            >
              <Icon name="clock" className="size-3.5" />
              {t('support.recompute')}
            </button>

            {recompute.isSuccess ? (
              <span className="text-ink-500 font-mono text-[10px]">{t('support.recomputed')}</span>
            ) : null}

            {promote.isError || recompute.isError ? (
              <span className="text-[11px] text-[color:var(--color-state-blocked)]">
                {(promote.error ?? recompute.error)?.message}
              </span>
            ) : null}
          </footer>
        )}
      </aside>
    </>
  )
}

/**
 * The promise, and whether it was kept.
 *
 * Every figure here is working minutes on the Sunday–Thursday week with Omani
 * public holidays removed (ADR-0009) — the server computes it, and this only
 * says which side of it the ticket is on.
 */
function Clocks({ ticket, asOf }: { ticket: Ticket; asOf: number }) {
  const { t, i18n } = useTranslation()

  if (ticket.sla === null) {
    return (
      <p className="border-line text-ink-500 mt-4 border-t pt-4 text-[12px]">
        {t('support.noClock')}
      </p>
    )
  }

  const rows = [
    {
      label: t('support.firstResponse'),
      due: ticket.sla.firstResponseDueAt,
      met: ticket.sla.firstRespondedAt,
      breached: ticket.slaInternal?.firstResponseBreached ?? false,
    },
    {
      label: t('support.resolution'),
      due: ticket.sla.resolutionDueAt,
      met: ticket.sla.resolvedAt,
      breached: ticket.slaInternal?.resolutionBreached ?? false,
    },
  ]

  return (
    <div className="border-line mt-4 flex flex-col gap-2 border-t pt-4">
      {rows.map((row) => {
        const overdue = row.met === null && row.due !== null && Date.parse(row.due) < asOf
        const state = row.met !== null
          ? { text: t('support.met'), tone: 'var(--color-state-done)' }
          : row.breached || overdue
            ? { text: t('support.breached'), tone: 'var(--color-state-blocked)' }
            : { text: t('support.onTrack'), tone: 'var(--color-ink-500)' }

        return (
          <div key={row.label} className="flex flex-wrap items-baseline gap-x-2.5 text-[12px]">
            <span className="text-ink-500 w-28">{row.label}</span>
            <span className="font-mono tabular-nums" style={{ color: state.tone }}>
              {state.text}
            </span>
            <span className="text-ink-500 font-mono text-[11px] tabular-nums">
              {(row.met ?? row.due) === null
                ? '—'
                : new Date((row.met ?? row.due) as string).toLocaleString(i18n.language)}
            </span>
          </div>
        )
      })}

      {ticket.sla.paused ? (
        <span className="text-ink-500 font-mono text-[10px] tracking-wide uppercase">
          {t('support.paused')}
        </span>
      ) : null}
    </div>
  )
}

function Thread({
  title,
  tone,
  rows,
}: {
  title: string
  tone: 'client' | 'internal'
  rows: { id: string; body: string; author: string | null; createdAt: string | null }[]
}) {
  const { t } = useTranslation()

  return (
    <section className="border-line mt-5 border-t pt-4">
      <h3 className="mb-2.5 flex items-center gap-2">
        <span className="text-ink-900 text-[13px] font-semibold">{title}</span>
        <span
          className={`rounded px-1.5 py-px font-mono text-[9px] tracking-wide uppercase ${
            tone === 'client' ? 'bg-brand-50 text-brand-700' : 'bg-surface-2 text-ink-500'
          }`}
        >
          {rows.length}
        </span>
      </h3>

      {rows.length === 0 ? (
        <p className="text-ink-500 text-[13px]">{t('support.noComments')}</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {rows.map((row) => (
            <li key={row.id} className="flex gap-2.5">
              <Avatar name={row.author} size="sm" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="text-ink-900 text-[12px] font-medium">{row.author ?? '—'}</span>
                  <span className="text-ink-500 font-mono text-[10px]">
                    {row.createdAt?.slice(0, 10) ?? ''}
                  </span>
                </div>
                <p className="text-ink-700 mt-0.5 text-[13px] whitespace-pre-line">{row.body}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
